import { inject, Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import {
  AccessEvalResult,
  AccessEvalStatus,
  EdgeNetInfo,
  GatePassage,
  HubConnectionStatus,
} from '../entities/access-edges.interface';
import { LocalStorageFacade } from '@application/localstorage';

interface EdgeOnlinePayload {
  edgeId?: string;
  apiKeyKind?: 0 | 1;
}

interface EdgeOfflinePayload {
  edgeId?: string;
}

interface HeartbeatPayload {
  edgeId?: string;
}

interface NetworkInfoPayload {
  edgeId?: string;
  edge?: string;
  id?: string;
  info?: EdgeNetInfo;
  net?: string | EdgeNetInfo;
}

interface GatePassagePayload {
  edgeId?: string;
  logicalDirection?: 0 | 1 | 2 | 3 | 'None' | 'Entry' | 'Exit' | 'Both';
  physicalDirection?: 0 | 1 | 'SideAtoB' | 'SideBtoA';
  passageCompleted?: boolean;
  isTriggeredByRelease?: boolean;
  releaseRequestId?: string;
  ts?: string | number;
  payload?: {
    logicalDirection?: 0 | 1 | 2 | 3 | 'None' | 'Entry' | 'Exit' | 'Both';
    physicalDirection?: 0 | 1 | 'SideAtoB' | 'SideBtoA';
    releaseRequestId?: string | null;
    isTriggeredByRelease?: boolean;
    passageCompleted?: boolean;
    payloadType?: number;
    version?: string | null;
    requestId?: string;
  };
}

interface AccessEvalResultPayload {
  edgeId?: string;
  status?: string;
  isAllowed?: boolean;
  logicalDirection?: string;
  personId?: string;
  name?: string | null;
  message?: string | null;
  effectiveUntilUtc?: string;
  logs?: any;
  error?: string | null;
  requestId?: string;
  ts?: string;
}

class AggressiveReconnectPolicy implements signalR.IRetryPolicy {
  private readonly maxDelayMs = 60000;
  private readonly baseDelayMs = 500;

  public nextRetryDelayInMilliseconds(
    retryContext: signalR.RetryContext
  ): number {
    if (retryContext.previousRetryCount === 0) {
      return 0;
    }

    const attempt = retryContext.previousRetryCount - 1;
    const delay = this.baseDelayMs * Math.pow(2, attempt);

    return Math.min(delay, this.maxDelayMs);
  }
}

export interface AccessPointEdgeOnlineEvent {
  edgeId: string;
  apiKeyKind?: 0 | 1;
}

export interface AccessPointEdgeOfflineEvent {
  edgeId: string;
}

export interface AccessPointEdgeHeartbeatEvent {
  edgeId: string;
}

export interface AccessPointEdgeNetworkInfoEvent {
  edgeId: string;
  net?: EdgeNetInfo | null;
  source: 'networkInfo' | 'netInfo';
}

export interface AccessPointEdgeGatePassageEvent {
  edgeId: string;
  lastGatePassage: GatePassage;
}

export interface AccessPointEdgeAccessEvalResultEvent {
  edgeId: string;
  accessEvalResult: AccessEvalResult;
}

@Injectable({ providedIn: 'root' })
export class AccessPointEdgeHubService implements OnDestroy {
  private readonly appConfig = inject(APP_CONFIG);
  private readonly apiBase = this.appConfig.apiUrl.access_edge_manager_service;
  private readonly localStorageService = inject(LocalStorageFacade);

  private connection: HubConnection | null = null;
  private healthCheckInterval: any = null;
  private isStarting = false;

  private readonly hubStatusSubject = new BehaviorSubject<HubConnectionStatus>(
    'disconnected'
  );
  public readonly hubStatus$: Observable<HubConnectionStatus> =
    this.hubStatusSubject.asObservable();

  private readonly edgeOnlineSubject =
    new Subject<AccessPointEdgeOnlineEvent>();
  public readonly edgeOnline$ = this.edgeOnlineSubject.asObservable();

  private readonly edgeOfflineSubject =
    new Subject<AccessPointEdgeOfflineEvent>();
  public readonly edgeOffline$ = this.edgeOfflineSubject.asObservable();

  private readonly heartbeatSubject =
    new Subject<AccessPointEdgeHeartbeatEvent>();
  public readonly heartbeat$ = this.heartbeatSubject.asObservable();

  private readonly networkInfoSubject =
    new Subject<AccessPointEdgeNetworkInfoEvent>();
  public readonly networkInfo$ = this.networkInfoSubject.asObservable();

  private readonly gatePassageSubject =
    new Subject<AccessPointEdgeGatePassageEvent>();
  public readonly gatePassage$ = this.gatePassageSubject.asObservable();

  private readonly accessEvalResultSubject =
    new Subject<AccessPointEdgeAccessEvalResultEvent>();
  public readonly accessEvalResult$ =
    this.accessEvalResultSubject.asObservable();

  public async start(): Promise<void> {
    if (
      this.isStarting ||
      this.connection?.state === HubConnectionState.Connected
    ) {
      return;
    }

    if (this.connection?.state === HubConnectionState.Connecting) {
      return;
    }

    this.isStarting = true;
    this.emitStatus('connecting');

    try {
      if (this.connection) {
        await this.stop();
      }

      const authorization = JSON.parse(
        this.localStorageService.getItem('authorization')
      );
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.apiBase}/hubs/edge-realtime`, {
          withCredentials: true,
          transport: HttpTransportType.WebSockets,
          skipNegotiation: true,
          accessTokenFactory: () => {
            return authorization?.Token || '';
          },
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect(new AggressiveReconnectPolicy())
        .build();

      this.setupHandlers();

      await this.connection.start();
      this.emitStatus('connected');

    } catch (error) {
      this.emitStatus('disconnected');
      throw error;
    } finally {
      this.isStarting = false;
    }
  }

  public async stop(): Promise<void> {
    this.emitStatus('disconnected');
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }

    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {}
      this.connection = null;
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  private setupHandlers(): void {
    if (!this.connection) return;

    this.connection.on('edgeOnline', (p: EdgeOnlinePayload | string) => {
      const edgeId = typeof p === 'string' ? p : p?.edgeId ?? '';
      const apiKeyKind = typeof p === 'object' ? p?.apiKeyKind : undefined;
      if (edgeId) {
        this.edgeOnlineSubject.next({ edgeId, apiKeyKind });
      }
    });

    this.connection.on('edgeOffline', (p: EdgeOfflinePayload | string) => {
      const edgeId = typeof p === 'string' ? p : p?.edgeId ?? '';
      if (edgeId) {
        this.edgeOfflineSubject.next({ edgeId });
      }
    });

    this.connection.on('heartbeat', (p: HeartbeatPayload | string) => {
      const edgeId = typeof p === 'string' ? p : p?.edgeId ?? '(?)';
      if (edgeId) {
        this.heartbeatSubject.next({ edgeId });
      }
    });

    this.connection.on('networkInfo', (evt: NetworkInfoPayload) => {
      this.handleNetworkInfo(evt, 'networkInfo');
    });

    this.connection.on('netInfo', (evt: NetworkInfoPayload) => {
      this.handleNetworkInfo(evt, 'netInfo');
    });

    this.connection.on('ack', () => {
      // no-op
    });

    this.connection.on('progress', () => {
      // no-op
    });

    this.connection.on('gatePassage', (evt: GatePassagePayload) => {
      this.handleGatePassage(evt);
    });

    this.connection.on('accessEvalResult', (evt: AccessEvalResultPayload) => {
      this.handleAccessEvalResult(evt);
    });

    this.connection.onclose(async (error) => {
      this.emitStatus('disconnected');
      if (error) {
      } else {
      }

      setTimeout(async () => {
        if (this.connection?.state === HubConnectionState.Disconnected) {
          try {
            await this.start();
          } catch (err) {}
        }
      }, 60000);
    });

    this.connection.onreconnecting((error) => {
      this.emitStatus('reconnecting');
      if (error) {
      }
    });

    this.connection.onreconnected(async (connectionId) => {
      this.emitStatus('connected');
    });
  }

  private handleNetworkInfo(
    evt: NetworkInfoPayload | string,
    source: 'networkInfo' | 'netInfo'
  ): void {
    const edgeId =
      typeof evt === 'string' ? evt : evt?.edgeId ?? evt?.edge ?? evt?.id ?? '';

    if (!edgeId) return;

    const netObj = this.coerceNet(evt);
    this.networkInfoSubject.next({ edgeId, net: netObj, source });
  }

  private coerceNet(evt: NetworkInfoPayload | string): EdgeNetInfo | null {
    if (!evt) return null;

    if (typeof evt === 'string') {
      try {
        return JSON.parse(evt) as EdgeNetInfo;
      } catch {
        return null;
      }
    }

    if (evt.info && typeof evt.info === 'object') {
      return evt.info as EdgeNetInfo;
    }

    if (typeof evt.net === 'string') {
      try {
        return JSON.parse(evt.net) as EdgeNetInfo;
      } catch {
        return null;
      }
    }

    if (evt.net && typeof evt.net === 'object') {
      return evt.net as EdgeNetInfo;
    }

    return null;
  }

  private handleGatePassage(evt: GatePassagePayload): void {
    const edgeId = evt?.edgeId;
    if (!edgeId) return;

    const logicalDirection =
      evt.payload?.logicalDirection ?? evt.logicalDirection;
    const physicalDirection =
      evt.payload?.physicalDirection ?? evt.physicalDirection;
    const passageCompleted =
      evt.payload?.passageCompleted ?? evt.passageCompleted;
    const isTriggeredByRelease =
      evt.payload?.isTriggeredByRelease ?? evt.isTriggeredByRelease;
    const releaseRequestId =
      evt.payload?.releaseRequestId ?? evt.releaseRequestId;
    const ts = evt.ts ?? new Date().toISOString();

    let normalizedLogicalDirection: number = 0;
    if (typeof logicalDirection === 'number') {
      normalizedLogicalDirection = logicalDirection;
    } else if (logicalDirection === 'Entry') {
      normalizedLogicalDirection = 1;
    } else if (logicalDirection === 'Exit') {
      normalizedLogicalDirection = 2;
    } else if (logicalDirection === 'Both') {
      normalizedLogicalDirection = 3;
    }

    let normalizedPhysicalDirection: number = 0;
    if (typeof physicalDirection === 'number') {
      normalizedPhysicalDirection = physicalDirection;
    } else if (physicalDirection === 'SideBtoA') {
      normalizedPhysicalDirection = 1;
    }

    const lastGatePassage: GatePassage = {
      edgeId,
      logicalDirection: normalizedLogicalDirection,
      physicalDirection: normalizedPhysicalDirection,
      passageCompleted: passageCompleted ?? false,
      isTriggeredByRelease: isTriggeredByRelease ?? false,
      releaseRequestId: releaseRequestId ?? null,
      ts,
      payload: {
        logicalDirection: normalizedLogicalDirection,
        physicalDirection: normalizedPhysicalDirection,
        releaseRequestId: releaseRequestId ?? null,
        isTriggeredByRelease: isTriggeredByRelease ?? false,
        passageCompleted: passageCompleted ?? false,
        payloadType: evt.payload?.payloadType ?? 0,
        version: evt.payload?.version ?? null,
        requestId: evt.payload?.requestId ?? '',
      },
    };

    this.gatePassageSubject.next({ edgeId, lastGatePassage });
  }

  private handleAccessEvalResult(evt: AccessEvalResultPayload): void {
    const edgeId = evt?.edgeId;
    if (!edgeId) return;

    const accessEvalResult: AccessEvalResult = {
      edgeId,
      status: (evt.status as AccessEvalStatus) ?? AccessEvalStatus.Error,
      isAllowed: evt.isAllowed ?? false,
      logicalDirection: evt.logicalDirection ?? '',
      personId: evt.personId ?? '',
      name: evt.name ?? null,
      message: evt.message ?? null,
      effectiveUntilUtc: evt.effectiveUntilUtc ?? '',
      logs: evt.logs ?? null,
      error: evt.error ?? null,
      requestId: evt.requestId ?? '',
      ts: evt.ts ?? new Date().toISOString(),
    };

    this.accessEvalResultSubject.next({ edgeId, accessEvalResult });
  }

  private emitStatus(status: HubConnectionStatus): void {
    this.hubStatusSubject.next(status);
  }

  ngOnDestroy(): void {
    this.stop();
    this.healthCheckInterval = null;
    this.hubStatusSubject.complete();
    this.edgeOnlineSubject.complete();
    this.edgeOfflineSubject.complete();
    this.heartbeatSubject.complete();
    this.networkInfoSubject.complete();
    this.gatePassageSubject.complete();
    this.accessEvalResultSubject.complete();
  }
}
